import type { ReactNode } from 'react'
import { CheckIcon } from '@heroicons/react/16/solid'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import type { DraftState, WizardStep } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { IssueList } from '../../../Services/OrderWizard/issues.ts'
import { STEP_LABELS, stepNavigation, stepStates, warningKeysOf } from '../../../Services/OrderWizard/issues.ts'
import { showTarget } from './StepIssues.tsx'
import { reviewStatus, stepSummaryLine } from '../../../Services/OrderWizard/stepSummaries.ts'
import { filesAtHandOf } from '../../../Flux/Selector/OrderWizard/OrderWizardSelectors.ts'
import type { SelectStep } from './stepSelect.ts'
import { plural } from '../../../Util/plural.ts'
import { BlockedReason } from './ReviewParts.tsx'

export const StepFooter = ({
  draft,
  step,
  steps,
  issues,
  primary,
  prompt,
  onSelect,
  onShowIssues
}: {
  draft: DraftState
  step: WizardStep
  steps: WizardStep[]
  issues: IssueList
  /** Whether Next is the step's primary action; when the step's content leads, Next stays secondary. */
  primary: boolean
  /** Asked before leaving, when a footer button was the way out. */
  prompt?: ReactNode
  onSelect: SelectStep
  onShowIssues: () => void
}) => {
  const dispatch = useDispatch()
  const state = stepStates(issues, [step])[0]
  const { next, previous, shortcut, allowed, anyway } = stepNavigation(issues, steps, step)
  /* Review answers for the whole order: blockers on any step and the check decide it, not its own issues */
  const review = step === 'review' ? reviewStatus(draft, issues, filesAtHandOf(draft)) : undefined
  const ok = review ? review.stage === 'passed' : state.state === 'ok'
  const summary = review ? review.text : stepSummaryLine(draft, issues, step)
  const goOn = (target: WizardStep) => {
    const keys = warningKeysOf(issues, step)
    onSelect(target, anyway ? () => dispatch(wizard.warningsAcknowledged({ draftId: draft.id, keys })) : undefined)
  }
  return (
    <div
      className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-b-xl border-t border-slate-200 bg-white/95 px-4 py-2.5 backdrop-blur sm:px-6"
      data-testid="wizard-step-footer"
    >
      {prompt}
      {ok ? (
        <p className="flex min-w-0 flex-1 items-start gap-1 text-sm text-slate-600" data-testid="wizard-step-ok">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />{' '}
          <span className="min-w-0 break-words" data-testid="wizard-step-summary">
            {summary} — looks good
          </span>
        </p>
      ) : (
        <p className="min-w-0 flex-1 break-words text-sm text-slate-600" data-testid="wizard-step-summary">
          {review?.stage === 'blocked' ? <BlockedReason reason={summary} issues={issues} onShowIssues={onShowIssues} /> : summary}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {!allowed && !review && (
          <button
            type="button"
            className="text-xs text-red-600 hover:underline"
            data-testid="wizard-next-blocked"
            onClick={() => showTarget(issues.blockers.find((issue) => issue.step === step)?.target)}
          >
            Fix {plural(state.blockers, 'issue')} above
          </button>
        )}
        {next && next !== 'review' && (
          <button
            type="button"
            className="link link-hover px-1 text-sm text-slate-500"
            data-testid="wizard-jump-review"
            onClick={() => onSelect('review')}
          >
            Jump to review
          </button>
        )}
        {previous && (
          <button type="button" className="btn btn-sm btn-ghost" data-testid="wizard-back" onClick={() => onSelect(previous)}>
            Back
          </button>
        )}
        {next && (
          <button
            type="button"
            className={`btn btn-sm ${primary ? 'btn-primary' : 'btn-outline'}`}
            disabled={!allowed}
            data-testid="wizard-next"
            onClick={() => goOn(next)}
          >
            {shortcut ? STEP_LABELS.review : anyway ? 'Next anyway' : `Next: ${STEP_LABELS[next]}`}
          </button>
        )}
      </div>
    </div>
  )
}
